export type PresetEvidenceTier = 'official' | 'product-recommendation' | 'derived'

export interface PresetSafeZone {
  x: number
  y: number
  width: number
  height: number
  tier: 'official' | 'derived'
  note: string
}

export interface SocialResizePreset {
  id:
    | 'vk-square-post'
    | 'vk-landscape-post'
    | 'telegram-story'
    | 'youtube-thumbnail'
    | 'youtube-banner'
    | 'universal-avatar'
  platform: string
  surface: string
  width: number
  height: number
  formats: readonly ('image/jpeg' | 'image/png')[]
  maxBytes: number | null
  safeZone: PresetSafeZone | null
  previewMask: 'circle' | null
  sourceUrl: string
  sourceTitle: string
  sourceTier: PresetEvidenceTier
  sourceUpdatedAt: string | null
  verifiedAt: string
  evidenceNote: string
}

const VK_SOURCE =
  'https://object2-ac.vk-apps.com/vk-education-0554ff56-27eb-4093-a702-685722cef6d9/media/7f/9e7fbe4c4e634b8cbbc1b47dea5f29c6.pdf'
const TELEGRAM_STORY_SOURCE = 'https://core.telegram.org/bots/api#inputstorycontentphoto'
const YOUTUBE_THUMBNAIL_SOURCE = 'https://support.google.com/youtube/answer/72431?hl=ru'
const YOUTUBE_BRANDING_SOURCE = 'https://support.google.com/youtube/answer/10456525?hl=ru'
const IMGIFY_SOURCE = 'https://imgify.ru/razmer-foto-dlya-socsetey'

export const SOCIAL_RESIZE_PRESETS = [
  {
    id: 'vk-square-post',
    platform: 'VK',
    surface: 'Квадратный пост',
    width: 1080,
    height: 1080,
    formats: ['image/jpeg', 'image/png'],
    maxBytes: null,
    safeZone: null,
    previewMask: null,
    sourceUrl: VK_SOURCE,
    sourceTitle: 'VK Education: требования к изображениям AdBlogger',
    sourceTier: 'product-recommendation',
    sourceUpdatedAt: null,
    verifiedAt: '2026-07-25',
    evidenceNote: 'Рекомендация imgify внутри опубликованного VK диапазона и пропорции 1:1.',
  },
  {
    id: 'vk-landscape-post',
    platform: 'VK',
    surface: 'Горизонтальный пост',
    width: 1920,
    height: 1080,
    formats: ['image/jpeg', 'image/png'],
    maxBytes: null,
    safeZone: null,
    previewMask: null,
    sourceUrl: VK_SOURCE,
    sourceTitle: 'VK Education: требования к изображениям AdBlogger',
    sourceTier: 'product-recommendation',
    sourceUpdatedAt: null,
    verifiedAt: '2026-07-25',
    evidenceNote: 'Рекомендация imgify внутри опубликованного VK диапазона и пропорции 16:9.',
  },
  {
    id: 'telegram-story',
    platform: 'Telegram',
    surface: 'История',
    width: 1080,
    height: 1920,
    formats: ['image/jpeg'],
    maxBytes: 10 * 1024 * 1024,
    safeZone: null,
    previewMask: null,
    sourceUrl: TELEGRAM_STORY_SOURCE,
    sourceTitle: 'Telegram Bot API: InputStoryContentPhoto',
    sourceTier: 'official',
    sourceUpdatedAt: null,
    verifiedAt: '2026-07-25',
    evidenceNote: 'Официальное точное полотно 1080×1920 и лимит 10 MB.',
  },
  {
    id: 'youtube-thumbnail',
    platform: 'YouTube',
    surface: 'Обложка видео',
    width: 3840,
    height: 2160,
    formats: ['image/jpeg', 'image/png'],
    maxBytes: 50 * 1024 * 1024,
    safeZone: null,
    previewMask: null,
    sourceUrl: YOUTUBE_THUMBNAIL_SOURCE,
    sourceTitle: 'YouTube Help: как добавить значок видео',
    sourceTier: 'official',
    sourceUpdatedAt: null,
    verifiedAt: '2026-07-25',
    evidenceNote: 'Официальная рекомендация 3840×2160; на мобильной загрузке действует лимит 2 MB.',
  },
  {
    id: 'youtube-banner',
    platform: 'YouTube',
    surface: 'Баннер канала',
    width: 2560,
    height: 1440,
    formats: ['image/jpeg', 'image/png'],
    maxBytes: 6 * 1024 * 1024,
    safeZone: {
      x: (2560 - 1235 * 1.25) / 2 / 2560,
      y: (1440 - 338 * 1.25) / 2 / 1440,
      width: (1235 * 1.25) / 2560,
      height: (338 * 1.25) / 1440,
      tier: 'derived',
      note: 'Пунктирная зона пропорционально пересчитана с официального минимума 2048×1152.',
    },
    previewMask: null,
    sourceUrl: YOUTUBE_BRANDING_SOURCE,
    sourceTitle: 'YouTube Help: управление оформлением канала',
    sourceTier: 'official',
    sourceUpdatedAt: null,
    verifiedAt: '2026-07-25',
    evidenceNote: 'Официальное рекомендованное полотно; safe-zone является производной.',
  },
  {
    id: 'universal-avatar',
    platform: 'Универсальный',
    surface: 'Аватар',
    width: 1024,
    height: 1024,
    formats: ['image/jpeg', 'image/png'],
    maxBytes: null,
    safeZone: null,
    previewMask: 'circle',
    sourceUrl: IMGIFY_SOURCE,
    sourceTitle: 'Рекомендация imgify для универсального аватара',
    sourceTier: 'product-recommendation',
    sourceUpdatedAt: '2026-07-25',
    verifiedAt: '2026-07-25',
    evidenceNote: 'Продуктовая рекомендация; круг — только маска предпросмотра.',
  },
] as const satisfies readonly SocialResizePreset[]

export type SocialResizePresetId = (typeof SOCIAL_RESIZE_PRESETS)[number]['id']

export function socialResizePreset(id: SocialResizePresetId): SocialResizePreset {
  const preset = SOCIAL_RESIZE_PRESETS.find((candidate) => candidate.id === id)
  if (!preset) throw new Error('unknown_preset')
  return preset
}
